import {
    Button,
    Card,
    CardContent,
    CardHeader,
    colors,
    Grid,
    makeStyles,
    Switch,
    TextField,
    Theme,
    Typography
} from '@material-ui/core';
import {useSnackbar} from 'notistack';
import React, {useEffect, useState} from 'react';
import useRouter from 'use-react-router';
import validate from 'validate.js';
import {Can} from '../../../components/Can';
import ConfirmDelete from '../../../components/ConfirmDelete';
import {LoadingOverlay} from '../../../components/LoadingOverlay/LoadingOverlay';
import {RoomParty} from '../../../graphql/groups.datatypes';
import anyLoading from '../../../utils/anyLoading';
import {getHandleFieldChange, getHasError} from '../../../utils/formHelpers';
import createRoomParty, {CreateRoomPartyInput} from './createRoomParty.api';
import deleteRoomParty from './deleteRoomParty.api';
import updateRoomParty, {UpdateRoomPartyInput} from './updateRoomParty.api';
import moment, {Moment} from 'moment';
import AvailableHotelModal from '../../Resources/AvailableHotelModal';
import {Resource} from '../../../graphql/resources.datatypes';
import {getGroupNightsData} from '../../BookingTool/helperFunctions';
import {formatDate, formatGraphQlDate} from '../../../utils/formatters';
import {WCVDatePicker} from '../../../components/DateUtilsProvider/WCVDatePicker';

const useStyles = makeStyles((theme: Theme) => ({
    container: {
        marginTop: theme.spacing(3),
        width: '100%',
        marginBottom: theme.spacing(2),
    },
    card: {},
    saveButton: {
        marginLeft: theme.spacing(1),
        color: theme.palette.white,
        backgroundColor: colors.green[600],
        '&:hover': {
            backgroundColor: colors.green[900]
        }
    },
    deleteButton: {
        marginLeft: theme.spacing(1),
        color: theme.palette.white,
        backgroundColor: theme.palette.error.light,
        '&:hover': {
            backgroundColor: theme.palette.error.main
        }
    },
    cloneButton: {
        marginLeft: theme.spacing(1),
        color: theme.palette.white,
        backgroundColor: colors.green[400],
        '&:hover': {
            backgroundColor: colors.green[700]
        }
    },
    errorIcon: {
        marginRight: theme.spacing(1),
        color: theme.palette.error.main,
    },
    marginBottom: {
        paddingLeft: theme.spacing(1),
        marginBottom: theme.spacing(3),
    },
    linkButton: {
        marginRight: theme.spacing(1),
    },
    tabs: {},
}));


export const RoomPartyEdit = (props: { roomParty: RoomParty, isAdmin: boolean, canEdit: boolean }) => {
    const classes = useStyles();
    const {roomParty, isAdmin, canEdit} = props;
    const isNewParty = roomParty.id === -1;
    const {history} = useRouter();
    const {enqueueSnackbar} = useSnackbar();
    const [sendCreateRoomParty, createData] = createRoomParty();
    const [sendUpdateRoomParty, updateData] = updateRoomParty();
    const [sendDeleteRoomParty, deleteData] = deleteRoomParty();
    const [groupNights, setGroupNights] = useState(getGroupNightsData(roomParty.booking!.group!));
    const totalNightsAllowed = groupNights.mandatoryNightsCount + groupNights.maxAdditionalNights;

    const isExistingDay = (date: Moment) => {
        const start = moment(new Date(roomParty.start_date!));
        const end = moment(new Date(roomParty.end_date!));
        return date.isSameOrAfter(start, 'days') && date.isBefore(end, 'days')
    }

    const checkRoomAvailable = (hotel: Resource, start: Moment, end: Moment, capacity: number) => {
        const dayCount = end.diff(start, 'days');
        const passed = hotel.roomResources && hotel.roomResources
            .filter(roomResource => (capacity !== undefined && capacity > 0) ? roomResource.guest_capacity! >= capacity : true)
            .map(roomResource => {
                if (!roomResource.resource_banks) return false;
                const dayRange = roomResource.resource_banks && roomResource.resource_banks
                    .filter(resourceBank => {
                        const date = moment(new Date(resourceBank.date!));
                        return date.isSameOrAfter(start, 'days') && date.isBefore(end, 'days') && (resourceBank.remaining! > 0 || isExistingDay(date));
                    });
                return roomResource.allow_manual_overbooking || (dayRange && dayRange.length === dayCount);
            })
            .find(pass => pass === true);
        return passed === true;
    };

    //1. Set form and error state
    const [formState, setFormState] = useState({...roomParty, notes: roomParty.notes ? roomParty.notes : ''});
    const loadingData = anyLoading(createData, updateData, deleteData);
    const [errors, setErrors] = useState({
        isValid: false,
        roomAvailable: false,
        rangeInvalid: false,
        errors: {
            name: false,
            description: false,
            start_date: false,
            end_date: false,
            admissions_cutoff: false,
        }
    });

    useEffect(() => {
        setFormState({...roomParty, notes: roomParty.notes ? roomParty.notes : ''});
        setGroupNights(getGroupNightsData(roomParty.booking!.group!));
    }, [roomParty]);

    //2. Setup helper functions for form
    const handleFieldChange = getHandleFieldChange(setFormState);
    const hasError = getHasError(errors);

    //3. setup validation and error hook
    const validateSchema: any = {};

    useEffect(() => {
        const newErrors = validate(formState, validateSchema);
        const start = moment(new Date(formState.start_date!));
        const end = moment(new Date(formState.end_date!));
        const bookingLength = end.diff(start, 'days');

        console.log(formState);
        const roomAvailable = checkRoomAvailable(
            formState.hotel!,
            start,
            end,
            formState.members!.length
        );

        const rangeInvalid = start.isAfter(groupNights.mandatoryStart, 'days') ||
            end.isBefore(groupNights.mandatoryEnd, 'days') ||
            bookingLength > totalNightsAllowed;

        setErrors(state => ({
            ...state,
            roomAvailable,
            rangeInvalid,
            isValid: !newErrors && roomAvailable && !rangeInvalid,
            errors: newErrors || {}
        }));
    }, [formState]);

    //4. Handle save form
    const onSaveClick = () => {
        if (isNewParty) {
            const sanitisedData: CreateRoomPartyInput = {
                data: {
                    group_booking_id: formState.group_booking_id!,
                    hotel_resource_id: formState.hotel_resource_id!,
                    disabled: formState.disabled!,
                    start_date: formState.start_date ? formatGraphQlDate(formState.start_date)! : '',
                    end_date: formState.end_date ? formatGraphQlDate(formState.end_date)! : '',
                    wheelchair_access: formState.wheelchair_access!,
                    has_infants: formState.has_infants!,
                    notes: formState.notes ? formState.notes : 'n/a',
                    members_different_dates: formState.members_different_dates!,
                }
            };

            sendCreateRoomParty({variables: sanitisedData})
                .then((response) => {
                    enqueueSnackbar('Room Party Saved.', {variant: 'success'});
                    history.push('/room_party/' + response.data!.createRoomParty.id);
                })
                .catch(() => {
                    enqueueSnackbar('Save Room Party Failed!', {variant: 'error'});
                });
        } else {
            const sanitisedData: UpdateRoomPartyInput = {
                data: {
                    id: formState.id,
                    group_booking_id: formState.group_booking_id!,
                    hotel_resource_id: formState.hotel_resource_id!,
                    disabled: formState.disabled!,
                    start_date: formState.start_date && !roomParty.members_different_dates ? formatGraphQlDate(formState.start_date) : undefined,
                    end_date: formState.end_date && !roomParty.members_different_dates ? formatGraphQlDate(formState.end_date) : undefined,
                    wheelchair_access: formState.wheelchair_access!,
                    has_infants: formState.has_infants!,
                    notes: formState.notes!,
                    members_different_dates: formState.members_different_dates!,
                }
            };

            sendUpdateRoomParty({variables: sanitisedData})
                .then(() => {
                    enqueueSnackbar('Room Party Updated.', {variant: 'success'});
                })
                .catch(() => {
                    enqueueSnackbar('Room Party Update Failed!', {variant: 'error'});
                });
        }
    };

    //Delete modal functions
    const [open, setOpen] = useState(false);
    const handleClickOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleDelete = () => {
        sendDeleteRoomParty({variables: {id: formState.id}})
            .then(() => {
                enqueueSnackbar('Room Party Deleted.', {variant: 'success'});
                history.push('/groups');
            })
            .catch(() => {
                enqueueSnackbar('Deleted Room Party Failed!', {variant: 'error'});
            });

        handleClose();
    };

    //change hotel functions.
    const [openHotelPopup, setOpenHotelPopup] = useState(false);
    const handleOpenHotelPopup = () => {
        setOpenHotelPopup(true);
    };

    const handleCloseHotelPopup = () => {
        setOpenHotelPopup(false);
    };

    const handleConfirmHotelId = (hotel?: Resource) => {
        setFormState({...formState, hotel_resource_id: !hotel ? null : hotel.id, hotel});
        handleCloseHotelPopup();
    };


    if (!roomParty.booking) return <div>loading...</div>

    return (
        <form>
            <LoadingOverlay loading={loadingData.loading}>

                <Card className={classes.container}>
                    <CardHeader title="Room Party Details" action={[
                        isAdmin && (<Can key={2} permission={['room_party.change_hotel']}>
                            <Button variant="contained" className={classes.linkButton} onClick={handleOpenHotelPopup}>Change
                                Hotel</Button>
                        </Can>)
                    ]}/>
                    <CardContent className={classes.card}>
                        <Grid container spacing={3}>

                            <Grid item xs={12}>
                                    <Grid container spacing={3}>
                                        {!errors.roomAvailable && (
                                            <Grid item xs={12}>
                                                <Typography variant={'body1'} color={'error'}>
                                                    Room Unavailable in hotel for these dates.
                                                </Typography>
                                            </Grid>
                                        )}
                                        {errors.rangeInvalid && (
                                            <Grid item xs={12}>
                                                <Typography variant={'body1'} color={'error'}>
                                                    Date range is invalid. Group stay constraints: <br/>
                                                    Mandatory start: {formatDate(groupNights.mandatoryStart)} <br/>
                                                    Mandatory end: {formatDate(groupNights.mandatoryEnd)}<br/>
                                                    Max Stay: {totalNightsAllowed} nights. <br/>
                                                </Typography>
                                            </Grid>
                                        )}
                                        <Grid item md={4} xs={12}>
                                            {!isAdmin && (
                                                <Typography variant="body1">Hotel: {formState.hotel && formState.hotel.display_name}</Typography>
                                            )}
                                            {isAdmin && (
                                                <TextField
                                                    fullWidth
                                                    label="Hotel"
                                                    name="hotel"
                                                    value={!formState.hotel ? 'No Hotel' : formState.hotel.internal_name}
                                                    variant="outlined"
                                                    margin="normal"
                                                    disabled={true}
                                                />
                                            )}

                                            <Can permission={['room_party.change_hotel']}>
                                                {openHotelPopup && (
                                                    <AvailableHotelModal
                                                        onClose={handleCloseHotelPopup}
                                                        onConfirmClick={handleConfirmHotelId}
                                                        open={openHotelPopup}
                                                        startDate={moment(new Date(roomParty.start_date!))}
                                                        endDate={moment(new Date(roomParty.end_date!))}
                                                        tourResourceId={roomParty.booking.group!.tour_resource_id!}
                                                        guestCapacity={roomParty.members!.length}
                                                        currentHotelId={roomParty.hotel_resource_id!}
                                                        isRoomParty={true}
                                                    />
                                                )}
                                            </Can>
                                        </Grid>
                                        <Grid item md={4} xs={6}>
                                            {!isAdmin && (
                                                <Typography variant="body1">Check In: {formState.start_date && formatDate(formState.start_date)}</Typography>
                                            )}
                                            {isAdmin && (
                                                <WCVDatePicker
                                                    {...hasError('start_date')}
                                                    id="start_date"
                                                    label="Check In:"
                                                    value={formState.start_date}
                                                    onChange={(date, value) => {
                                                        setFormState({
                                                            ...formState,
                                                            start_date: value ? value : undefined
                                                        })
                                                    }}
                                                    disabled={formState.disabled || roomParty.members_different_dates || !(canEdit || isAdmin)}
                                                />
                                            )}
                                        </Grid>
                                        <Grid item md={4} xs={6}>
                                            {!isAdmin && (
                                                <Typography variant="body1">Check Out: {formState.end_date && formatDate(formState.end_date)}</Typography>
                                            )}
                                            {isAdmin && (
                                                <WCVDatePicker
                                                    {...hasError('end_date')}
                                                    id="end_date"
                                                    label="Check Out:"
                                                    value={formState.end_date}
                                                    onChange={(date, value) => {
                                                        setFormState({
                                                            ...formState,
                                                            end_date: value ? value : undefined
                                                        })
                                                    }}
                                                    disabled={formState.disabled || roomParty.members_different_dates || !(canEdit || isAdmin)}
                                                />
                                            )}
                                        </Grid>
                                    </Grid>
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    {...hasError('notes')}
                                    fullWidth
                                    label="Room requests"
                                    name="notes"
                                    multiline
                                    rows={2}
                                    rowsMax={4}
                                    onChange={handleFieldChange}
                                    value={formState.notes}
                                    variant="outlined"
                                    disabled={!isAdmin || formState.disabled}
                                />
                            </Grid>

                            {isAdmin && (
                                <Grid item md={3} xs={6}>
                                    <Grid component="label" container alignItems="center" spacing={1}>
                                        <Grid item>
                                            <Switch
                                                checked={formState.wheelchair_access}
                                                color="secondary"
                                                edge="start"
                                                name="wheelchair_access"
                                                onChange={handleFieldChange}
                                                value={formState.wheelchair_access}
                                                disabled={formState.disabled || !(canEdit || isAdmin)}
                                            />
                                        </Grid>
                                        <Grid item><Typography variant="h5">Wheelchair Access?</Typography></Grid>
                                    </Grid>
                                </Grid>
                            )}
                            {isAdmin && (
                                <Grid item md={3} xs={6}>
                                    <Grid component="label" container alignItems="center" spacing={1}>
                                        <Grid item>
                                            <Switch
                                                checked={formState.has_infants}
                                                color="secondary"
                                                edge="start"
                                                name="has_infants"
                                                onChange={handleFieldChange}
                                                value={formState.has_infants}
                                                disabled={formState.disabled || !(canEdit || isAdmin)}
                                            />
                                        </Grid>
                                        <Grid item><Typography variant="h5">Has Infants?</Typography></Grid>
                                    </Grid>
                                </Grid>
                            )}
                            {isAdmin && !isNewParty && (
                                <Grid item md={3} xs={6}>
                                    <Grid component="label" container alignItems="center" spacing={1}>
                                        <Grid item>
                                            <Switch
                                                checked={formState.members_different_dates}
                                                color="secondary"
                                                edge="start"
                                                name="members_different_dates"
                                                onChange={handleFieldChange}
                                                value={formState.members_different_dates}
                                                disabled={formState.disabled || !(canEdit || isAdmin)}
                                            />
                                        </Grid>
                                        <Grid item><Typography variant="h5">Guests with varied
                                            dates?</Typography></Grid>
                                    </Grid>
                                </Grid>
                            )}

                            {isAdmin && (
                                <Grid item md={3} xs={6}>
                                    <Grid component="label" container alignItems="center" spacing={1}>
                                        <Grid item>
                                            <Switch
                                                checked={formState.disabled}
                                                color="secondary"
                                                edge="start"
                                                name="disabled"
                                                onChange={handleFieldChange}
                                                value={formState.disabled}
                                            />
                                        </Grid>
                                        <Grid item><Typography variant="h5">Disabled?</Typography></Grid>
                                    </Grid>
                                </Grid>
                            )}

                            <Grid item xs={12}>
                                <Button className={classes.saveButton} onClick={onSaveClick} variant="contained"
                                        disabled={!errors.isValid}>Save</Button>

                                {isAdmin && (
                                    <Can permission="groups.delete">
                                        <Button onClick={handleClickOpen} className={classes.deleteButton}
                                                variant="contained"
                                                disabled={isNewParty || (roomParty.members && roomParty.members.length > 0)}>
                                            Delete
                                        </Button>
                                    </Can>
                                )}

                            </Grid>

                        </Grid>
                    </CardContent>
                </Card>

            </LoadingOverlay>

            <ConfirmDelete onClose={handleClose} onDeleteClick={handleDelete} open={open}/>
        </form>
    );
};

export default RoomPartyEdit;
